/**
 * Authentication constants for the application
 *
 * This file defines all public and protected paths and endpoints
 * used for authentication throughout the application.
 */

// Static assets and system paths that should always be public
export const STATIC_PUBLIC_PATHS = [
  '/favicon.ico',
  '/icon.png',
  '/assets',
  '/images',
] as const

// Auth pages - pages that authenticated users should be redirected away from
export const AUTH_REDIRECT_PAGES = [
  '/login',
  '/register',
  '/register/confirmation',
  '/login/confirmation',
  '/forgot-password',
  '/reset-password',
  '/auth/callback',
  '/auth/google/callback',
  '/auth/google-calendar-callback',
  '/auth/gmail-callback',
  '/auth/outlook-callback',
  '/auth/outlook-calendar-callback',
] as const

// Public pages - accessible by anyone (authenticated or not), no redirects
export const PUBLIC_ACCESS_PATHS = [
  '/',
  '/about-scrambl', // Native About window
  '/f/', // Public forms
  '/embed/', // Embedded forms
  '/invite', // Org invites
  '/client-portal/invite', // Client portal invites
  '/portal/', // Magic-link ticket portal
  '/csat/', // Public CSAT survey (token is the credential)
  '/unsubscribe/', // Public email unsubscribe landing (token is the credential)
  '/beta', // Beta Services Addendum, linked from the sign-in footer
  '/dpa', // Data Processing Addendum, linked from workspace setup
] as const

// Combined auth-related public paths (for backwards compatibility)
export const AUTH_PUBLIC_PATHS = [
  ...AUTH_REDIRECT_PAGES,
  ...PUBLIC_ACCESS_PATHS,
] as const

// API endpoints categorized by auth requirement
export const AUTH_ENDPOINTS = {
  PUBLIC: [
    // Authentication endpoints
    '/auth/register',
    '/auth/login',
    '/auth/confirm',
    '/auth/verify-login',
    '/auth/resend-code',
    '/auth/refresh',
    '/auth/magic-link',
    '/auth/password-login',
    '/auth/password/forgot',
    '/auth/password/reset',
    '/auth/logout',
    '/auth/check-email-exists',

    // Health check endpoints
    '/health',
    '/health/cors',
  ],
  PROTECTED: ['/auth/me'],
} as const

// Combined public paths for middleware
export const ALL_PUBLIC_PATHS = [
  ...STATIC_PUBLIC_PATHS,
  ...AUTH_PUBLIC_PATHS,
  ...AUTH_ENDPOINTS.PUBLIC,
] as const

/**
 * Whether `path` is `base` or below it. Entries ending in "/" are prefixes
 * ("/f/" covers every form); others match whole segments, so "/beta" or
 * "/invite" never claims an org slug like "/betacorp/today".
 */
export function matchesPublicPath(path: string, base: string): boolean {
  if (base === '/') return path === '/' || path.startsWith('/?')
  if (base.endsWith('/')) return path.startsWith(base)
  return (
    path === base || path.startsWith(`${base}/`) || path.startsWith(`${base}?`)
  )
}

// Utility function to check if a path is public
export function isPublicPath(path: string): boolean {
  return ALL_PUBLIC_PATHS.some(
    publicPath =>
      typeof publicPath === 'string' && matchesPublicPath(path, publicPath),
  )
}

// Type exports
export type PublicEndpoint = (typeof AUTH_ENDPOINTS.PUBLIC)[number]
export type PublicPath = (typeof ALL_PUBLIC_PATHS)[number]
